import React, { useState, useEffect } from 'react';
import { View, Picker, Item, Text, Flex, Button } from '@adobe/react-spectrum';
import { QuillEditor } from './QuillEditor';
import { ValidationTooltip } from './ValidationTooltip';
import { FormField as StoreFormField } from '../../store/slice';
import styles from '../FormField.module.scss';
import { fetchDropdownOptions } from '../../store/api';
import { Key } from '@react-types/shared';
import EditIn from '@spectrum-icons/workflow/EditIn';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';

interface DropdownOption {
  id: string;
  name: string;
}

interface DropdownWithRichTextEditorProps {
  field: StoreFormField;
  value: {
    option: string;
    content: string;
  };
  onChange: (name: string, value: any) => void;
  validationState?: 'valid' | 'invalid';
  errorMessage?: string;
}

export const DropdownWithRichTextEditor: React.FC<
  DropdownWithRichTextEditorProps
> = ({
  field,
  value = { option: '', content: '' },
  onChange,
  validationState,
  errorMessage
}) => {
  const [selectedOption, setSelectedOption] = useState<string>(
    value.option || ''
  );
  const [editorContent, setEditorContent] = useState(value.content || '');
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [charCount, setCharCount] = useState(0);

  // Update charCount when content changes
  useEffect(() => {
    // Remove trailing newline if present
    const contentLength = editorContent
      ? editorContent.length - (editorContent.endsWith('\n') ? 1 : 0)
      : 0;
    setCharCount(contentLength);
  }, [editorContent]);

  // Fetch dropdown options
  useEffect(() => {
    if (field.api) {
      setIsLoading(true);
      fetchDropdownOptions(field.api)
        .then(data => {
          setOptions(data);
          setIsLoading(false);
        })
        .catch(error => {
          setIsLoading(false);
        });
    } else if (field.options) {
      // Handle direct options if provided instead of API
      const formattedOptions = field.options.map(opt => ({
        id: opt.value,
        name: opt.label
      }));
      setOptions(formattedOptions);
    } else {
      // Add fallback options for testing
      setOptions([
        { id: 'test1', name: 'Test Option 1' },
        { id: 'test2', name: 'Test Option 2' },
        { id: 'test3', name: 'Test Option 3' }
      ]);
    }
  }, [field.api, field.name, field.options]);

  // Handle dropdown change
  const handleDropdownChange = (selected: Key) => {
    const selectedValue = selected.toString();
    setSelectedOption(selectedValue);

    // Update the combined value
    const newValue = {
      option: selectedValue,
      content: editorContent
    };

    onChange(field.name, newValue);
  };

  // Handle rich text editor change
  const handleEditorChange = (content: string) => {
    setEditorContent(content);

    // Update the combined value
    const newValue = {
      option: selectedOption,
      content: content
    };

    onChange(field.name, newValue);
  };

  // Toggle the editor visibility when selected
  const toggleEditorVisibility = () => {
    setIsEditorVisible(!isEditorVisible);
  };

  return (
    <View UNSAFE_className={styles.dropdownWithRichTextContainer}>
      <View UNSAFE_className={styles.dropdownContainer}>
        <div className={styles.ldapFieldWrapper}>
          <label className={styles.fieldLabel}>
            {field.label}
            {field.required && <span style={{ marginLeft: '2px' }}> *</span>}
          </label>
          <Picker
            aria-label={field.label}
            isDisabled={isLoading || options.length === 0}
            items={options}
            selectedKey={selectedOption}
            onSelectionChange={handleDropdownChange}
            placeholder="Select an option"
            width="100%"
            validationState={validationState}
          >
            {(item: DropdownOption) => <Item key={item.id}>{item.name}</Item>}
          </Picker>
        </div>
        <ValidationTooltip
          message="Please select an option from the dropdown first"
          isDisabled={!selectedOption}
        >
          <Button
            variant="primary"
            UNSAFE_className={`${styles.editorToggleButtonLarge} ${
              isEditorVisible
                ? styles.activeToggleButton
                : styles.editorTOggleButton
            } ${validationState === 'invalid' ? 'invalid' : ''}`}
            onPress={toggleEditorVisibility}
            isDisabled={!selectedOption}
          >
            <EditIn />
          </Button>
        </ValidationTooltip>
      </View>
      <Flex UNSAFE_className={styles.richTextSection}>
        {/* Rich Text Editor Section - Only visible when option selected and editor toggle is on */}
        {selectedOption && isEditorVisible && (
          <>
            <div className={styles.editorContainer}>
              <QuillEditor
                value={editorContent}
                onChange={handleEditorChange}
                placeholder="Enter details here..."
                charLimit={
                  typeof (field as any)?.charLimit === 'number' ||
                  (field as any)?.charLimit === null
                    ? (field as any).charLimit
                    : null
                }
                showCharCount={true}
                className={styles.fullEditor}
                onCharCountChange={setCharCount}
                minHeight="100px"
                footerLeft="Characters remaining"
              />
            </div>
            {/* Remove the static character limit div since we're showing it in the footer */}
          </>
        )}
      </Flex>
    </View>
  );
};
